import React, { useEffect, useId, useRef, useState } from 'react';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from './ui/combobox';
import { useApi, useAsync } from '../hooks';
import { debounce } from '../ui/utils';
import { cn } from '../lib/utils';

const optionLabel = (option) => {
  if (!option) {
    return '';
  }
  return option.nickname ? `${option.name} — ${option.nickname}` : option.name;
};

/* Debounced remote search against GET /customers?q= (UND-987), the existing-customer typeahead the
   spec calls for on both the submission form and the Change control. Same 500ms debounce as the
   legacy version this replaces, and the same controlled inputValue driving the search — the server
   has already done the filtering, so Base UI's own filtering is turned off (`filter={null}`) and
   `items` is exactly what the last search returned.

   The value this field produces is always a real CustomerListItem or null, never the typed text: an
   `onValueChange` that doesn't carry a `customerId` (Base UI's own null on Escape/clear, or a stray
   string) is normalized to null in `onChangeValue`. */
const CustomerAutocomplete = ({ value, onChangeValue, label = 'Existing Customer', helperText, disabled, placeholder, className }) => {
  const mounted = useRef(true);
  useEffect(() => () => (mounted.current = false), []);
  const inputId = useId();
  const { searchCustomers } = useApi();
  const [query, setQuery] = useState(value ? optionLabel(value) : '');
  const { value: results, execute, status } = useAsync(searchCustomers, { immediate: false });

  useEffect(() => {
    const debounceExecute = debounce(() => {
      if (mounted.current && query.trim()) {
        execute(query.trim());
      }
    }, 500);
    debounceExecute();
    return () => {
      debounceExecute.clear();
    };
  }, [query, execute]);

  // The dialog this lives in resets its own selection to null on every open (and "Make a new
  // customer" does the same) without unmounting this field, so the displayed text has to follow
  // `value` back to empty explicitly. Typing alone never touches `value` — only a later selection
  // does — so this never fights the user mid-search.
  useEffect(() => {
    setQuery(value ? optionLabel(value) : '');
  }, [value]);

  const items = query.trim() && Array.isArray(results) ? results : [];
  const emptyMessage = !query.trim() ? 'Type to search' : status === 'pending' ? 'Searching…' : 'No matches';

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">
          {label}
        </label>
      ) : null}
      <Combobox
        items={items}
        filter={null}
        value={value || null}
        onValueChange={(next) => onChangeValue(next && next.customerId ? next : null)}
        inputValue={query}
        onInputValueChange={(next) => {
          setQuery(next);
          // Emptying the text is clearing the field: it drops the selection too, rather than leaving
          // a customer picked behind an empty input.
          if (!next && value) {
            onChangeValue(null);
          }
        }}
        itemToStringLabel={optionLabel}
        isItemEqualToValue={(option, val) => !!val && !!option && option.customerId === val.customerId}
        disabled={disabled}
      >
        <ComboboxInput id={inputId} placeholder={placeholder || 'Search by name…'} disabled={disabled} showClear={!!value} />
        <ComboboxContent>
          <ComboboxList>
            {items.map((option) => (
              <ComboboxItem key={option.customerId} value={option}>
                {optionLabel(option)}
              </ComboboxItem>
            ))}
          </ComboboxList>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        </ComboboxContent>
      </Combobox>
      {helperText ? <p className="text-xs text-muted-foreground">{helperText}</p> : null}
    </div>
  );
};

export { CustomerAutocomplete };
